import {
  Box,
  Button,
  Typography,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Check from "@mui/icons-material/Check";
import React from "react";

interface StepperHeaderProps {
  /** zero‑based index of current step */
  stepIndex: number;
  /** list of all step titles */
  steps: string[];
  /** indices that are disabled / skipped */
  disabledSteps?: number[];
  onBack: () => void | Promise<void>;
  onNext: () => void | Promise<void>;
  onJumpTo: (index: number) => void;
}

export default function StepperHeader({
  stepIndex,
  steps,
  disabledSteps = [],
  onBack,
  onNext,
  onJumpTo,
}: StepperHeaderProps) {
  const theme   = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  /* dropdown state */
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenu  = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const handleSelect = (idx: number, disabled: boolean) => {
    if (!disabled) onJumpTo(idx);
    closeMenu();
  };

  const isDisabled = (idx: number) => disabledSteps.includes(idx);

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      px={isMobile ? 1 : 2}
      py={1.5}
      sx={{
        borderBottom: "3px solid #A020F0",
        backgroundColor:
          theme.palette.mode === "light" ? "#FFF" : theme.palette.background.paper,
      }}
    >
      {/* BACK */}
      <Button
        startIcon={<ChevronLeft />}
        disabled={stepIndex === 0}
        onClick={onBack}
        sx={{ minWidth: 64, px: 1, fontWeight: 600 }}
      >
        BACK
      </Button>

      {/* CENTER (clickable) */}
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        flexGrow={1}
        justifyContent="center"
        sx={{ cursor: "pointer" }}
        onClick={openMenu}
      >
        <Box
          sx={{
            bgcolor: "#A020F0",
            color: "#fff",
            borderRadius: "50%",
            width: 28,
            height: 28,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {stepIndex + 1}
        </Box>

        <Typography
          fontWeight={700}
          whiteSpace="nowrap"
          maxWidth={isMobile ? 120 : 240}
          overflow="hidden"
          textOverflow="ellipsis"
          title={steps[stepIndex]}
        >
          {steps[stepIndex]}
        </Typography>

        <ExpandMore sx={{ fontSize: 20, color: "#A020F0" }} />
      </Box>

      {/* NEXT */}
      <Button
        endIcon={<ChevronRight />}
        disabled={stepIndex === steps.length - 1}
        onClick={onNext}
        sx={{ minWidth: 64, px: 1, fontWeight: 600, color: "#A020F0" }}
      >
        NEXT
      </Button>

      {/* MENU */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        {steps.map((label, idx) => {
          const disabled  = isDisabled(idx);
          const completed = idx < stepIndex && !disabled;
          return (
            <MenuItem
              key={label}
              disabled={disabled}
              onClick={() => handleSelect(idx, disabled)}
              selected={idx === stepIndex}
              sx={{ minWidth: 200 }}
            >
              {completed ? (
                <Check fontSize="small" color="primary" sx={{ mr: 1 }} />
              ) : (
                <Typography
                  variant="caption"
                  sx={{ width: 18, mr: 1, textAlign: "center" }}
                >
                  {idx + 1}
                </Typography>
              )}
              {label}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}
